import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PlusCircle, Wrench, Cog, Palette, Edit, Send, MessageSquare, Users, Globe, Clock, RefreshCw, Upload, Download, FileSpreadsheet, Handshake, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  getMyListings, getMyEnquiries, respondEnquiry, acceptEnquiry, counterBidEnquiry, rejectCounterBid,
  publishListing, archiveListing, markListingSold, setListingVisibility, renewListing,
  getBulkImportTemplate, bulkImportListings, bulkRenewListings,
} from '@/lib/api';

const STATUS_LABEL = {
  draft: 'Entwurf', active: 'Aktiv', sold: 'Verkauft', expired: 'Abgelaufen', archived: 'Archiviert',
};
const STATUS_COLOR = {
  draft: 'secondary', active: 'default', sold: 'outline', expired: 'destructive', archived: 'outline',
};
const ENQUIRY_TYPE_LABEL = {
  general: 'Allgemein', inspection: 'Besichtigung', documents: 'Dokumente', photos: 'Fotos',
};

function _daysUntil(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  return Math.ceil((target - Date.now()) / (1000 * 60 * 60 * 24));
}

function ExpiryBadge({ listing }) {
  // Status badge already communicates `Abgelaufen` / `Verkauft` / `Archiviert` — only emit
  // the expiry countdown for active listings approaching their expires_at.
  if (listing.status !== 'active') return null;
  const days = _daysUntil(listing.expires_at);
  if (days === null) return null;
  if (days <= 14 && days >= 0) {
    return (
      <Badge variant="outline" className="gap-1 border-amber-400 text-amber-700 bg-amber-50 text-xs" data-testid={`expiry-badge-${listing.listing_id}`}>
        <Clock className="w-3 h-3" /> Läuft in {days}&nbsp;T.
      </Badge>
    );
  }
  return null;
}

export default function MyListingsPage() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [buyerEnqs, setBuyerEnqs] = useState([]);
  const [sellerEnqs, setSellerEnqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respOpen, setRespOpen] = useState(false);
  const [respEnq, setRespEnq] = useState(null);
  const [respMsg, setRespMsg] = useState('');
  // Renew dialog state
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewListingState, setRenewListingState] = useState(null);
  const [renewWithDiscount, setRenewWithDiscount] = useState(true);
  const [renewing, setRenewing] = useState(false);
  // Bulk import state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  // Bulk renew state
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkRenewOpen, setBulkRenewOpen] = useState(false);
  const [bulkRenewDiscount, setBulkRenewDiscount] = useState(true);
  const [bulkRenewing, setBulkRenewing] = useState(false);
  // Counter-bid dialog state
  const [counterOpen, setCounterOpen] = useState(false);
  const [counterEnq, setCounterEnq] = useState(null);
  const [counterRole, setCounterRole] = useState('seller'); // 'seller' | 'buyer'
  const [counterPrice, setCounterPrice] = useState('');
  const [counterQty, setCounterQty] = useState('');
  const [counterIncoterm, setCounterIncoterm] = useState('none');
  const [counterMessage, setCounterMessage] = useState('');
  const [counterSubmitting, setCounterSubmitting] = useState(false);

  const isRenewEligible = (l) => {
    if (l.status === 'expired') return true;
    if (l.status !== 'active') return false;
    const d = _daysUntil(l.expires_at);
    return d !== null && d <= 14;
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllEligible = () => {
    const eligible = listings.filter(isRenewEligible).map(l => l.listing_id);
    setSelectedIds(new Set(eligible));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const [l, bEnq, sEnq] = await Promise.all([
        getMyListings({}),
        getMyEnquiries({ role: 'buyer' }),
        getMyEnquiries({ role: 'seller' }),
      ]);
      setListings(l.data || []);
      setBuyerEnqs(bEnq.data || []);
      setSellerEnqs(sEnq.data || []);
    } catch (e) { toast.error('Laden fehlgeschlagen'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Deep-link: /my-listings?renew=<id> opens the renew dialog (from reminder email)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const renewId = params.get('renew');
    if (!renewId) return;
    params.delete('renew');
    window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`);
    // Wait until listings are loaded, then open dialog for the matching listing.
    const timer = setTimeout(() => {
      const match = listings.find(l => l.listing_id === renewId);
      if (match) openRenewDialog(match);
    }, 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings]);

  const handleAction = async (fn, id, msg) => {
    try { await fn(id); toast.success(msg); load(); } catch (e) { toast.error('Fehler'); }
  };

  const openRenewDialog = (listing) => {
    if (!isRenewEligible(listing)) {
      toast.info('Dieses Inserat benötigt aktuell keine Verlängerung.');
      return;
    }
    setRenewListingState(listing);
    setRenewWithDiscount(Boolean(listing.price_eur && listing.price_eur > 0));
    setRenewOpen(true);
  };

  const submitBulkRenew = async () => {
    if (selectedIds.size === 0) {
      toast.error('Keine Inserate ausgewählt');
      return;
    }
    setBulkRenewing(true);
    try {
      const ids = Array.from(selectedIds);
      const { data } = await bulkRenewListings(ids, {
        reducePricePercent: bulkRenewDiscount ? 10 : undefined,
      });
      toast.success(
        `${data.renewed_count} Inserate verlängert${data.skipped_count ? ` (${data.skipped_count} übersprungen)` : ''}`
      );
      setBulkRenewOpen(false);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk-Verlängerung fehlgeschlagen');
    } finally {
      setBulkRenewing(false);
    }
  };

  const submitRenew = async () => {
    if (!renewListingState) return;
    setRenewing(true);
    try {
      const pct = (renewWithDiscount && renewListingState.price_eur && renewListingState.price_eur > 0) ? 10 : undefined;
      await renewListing(renewListingState.listing_id, { reducePricePercent: pct });
      toast.success(pct ? 'Verlängert — Preis um 10 % reduziert' : 'Um 180 Tage verlängert');
      setRenewOpen(false);
      setRenewListingState(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verlängerung fehlgeschlagen');
    } finally {
      setRenewing(false);
    }
  };

  const downloadImportTemplate = async () => {
    try {
      const resp = await getBulkImportTemplate();
      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ersatzteile_vorlage.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Vorlage konnte nicht geladen werden');
    }
  };

  const submitBulkImport = async (dryRun = false) => {
    if (!importFile) { toast.error('Bitte CSV-Datei wählen'); return; }
    setImporting(true);
    try {
      const { data } = await bulkImportListings(importFile, dryRun);
      setImportResult(data);
      if (!dryRun) {
        toast.success(`${data.created} Ersatzteile importiert${data.error_count ? ` (${data.error_count} Fehler)` : ''}`);
        load();
      } else {
        toast.message(`Vorschau: ${data.valid_count} gültige, ${data.error_count} Fehler`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import fehlgeschlagen');
    } finally {
      setImporting(false);
    }
  };

  const resetImportDialog = () => {
    setImportFile(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleVisibility = async (l) => {
    const next = l.visibility === 'group_only' ? 'public' : 'group_only';
    try {
      await setListingVisibility(l.listing_id, next);
      toast.success(next === 'group_only' ? 'Nur Gruppen-sichtbar' : 'Öffentlich sichtbar');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fehler'); }
  };

  const submitResponse = async () => {
    if (!respEnq || !respMsg.trim()) return;
    try {
      await respondEnquiry(respEnq.enquiry_id, { response_message: respMsg });
      toast.success('Antwort gesendet');
      setRespOpen(false);
      setRespMsg('');
      setRespEnq(null);
      load();
    } catch (e) { toast.error('Fehler'); }
  };

  const handleAcceptEnquiry = async (enq) => {
    if (!window.confirm('Anfrage annehmen? Das Inserat wird als verkauft markiert (bzw. der Bestand reduziert) und eine Transaktion erstellt. Käufer und Verkäufer erhalten eine Bestätigungs-E-Mail.')) return;
    try {
      const res = await acceptEnquiry(enq.enquiry_id);
      toast.success('Anfrage angenommen – Transaktion erstellt');
      load();
      return res;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Anfrage konnte nicht angenommen werden');
    }
  };

  const openCounterDialog = (enq, role) => {
    setCounterEnq(enq);
    setCounterRole(role);
    // Prefill with current offer to give users a starting point
    const refPrice = enq.counter_bid_price_EUR ?? enq.bid_price_EUR ?? enq.system_price_EUR ?? '';
    const refQty = enq.counter_bid_qty ?? enq.requested_qty ?? '';
    setCounterPrice(refPrice != null ? String(refPrice) : '');
    setCounterQty(refQty != null ? String(refQty) : '');
    setCounterIncoterm(enq.counter_bid_incoterm || enq.incoterm || 'none');
    setCounterMessage('');
    setCounterOpen(true);
  };

  const submitCounterBid = async () => {
    if (!counterEnq) return;
    const priceNum = parseFloat(String(counterPrice).replace(',', '.'));
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      toast.error('Bitte einen gültigen Preis eingeben');
      return;
    }
    const qtyNum = counterQty ? parseFloat(String(counterQty).replace(',', '.')) : undefined;
    if (counterQty && (!Number.isFinite(qtyNum) || qtyNum <= 0)) {
      toast.error('Ungültige Menge');
      return;
    }
    setCounterSubmitting(true);
    try {
      await counterBidEnquiry(counterEnq.enquiry_id, {
        counter_bid_price_EUR: priceNum,
        counter_bid_qty: qtyNum,
        counter_bid_incoterm: counterIncoterm && counterIncoterm !== 'none' ? counterIncoterm : undefined,
        message: counterMessage || undefined,
      });
      toast.success('Gegengebot gesendet');
      setCounterOpen(false);
      setCounterEnq(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gegengebot konnte nicht gesendet werden');
    } finally {
      setCounterSubmitting(false);
    }
  };

  const handleRejectCounter = async (enq) => {
    if (!window.confirm('Gegengebot ablehnen? Die Anfrage wird geschlossen.')) return;
    try {
      await rejectCounterBid(enq.enquiry_id);
      toast.success('Gegengebot abgelehnt — Anfrage geschlossen');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ablehnung fehlgeschlagen');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-6xl" data-testid="my-listings-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Meine Inserate</h1>
            <p className="text-sm text-muted-foreground">Ersatzteile, Maschinen und Anfragen</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => { resetImportDialog(); setImportOpen(true); }} data-testid="bulk-import-btn">
              <Upload className="w-4 h-4 mr-2" /> CSV-Import
            </Button>
            <Button variant="outline" onClick={() => navigate('/listing/new?vertical=spare_parts')} data-testid="new-spare-part-btn">
              <Wrench className="w-4 h-4 mr-2" /> Neues Ersatzteil
            </Button>
            <Button onClick={() => navigate('/listing/new?vertical=machines')} data-testid="new-machine-btn">
              <Cog className="w-4 h-4 mr-2" /> Neue Maschine
            </Button>
            <Button variant="outline" onClick={() => navigate('/listing/new?vertical=paints')} data-testid="new-paint-btn">
              <Palette className="w-4 h-4 mr-2" /> Neue Farbe / Lack
            </Button>
          </div>
        </div>

        <Tabs defaultValue="listings">
          <TabsList className="mb-6">
            <TabsTrigger value="listings" data-testid="tab-listings">Inserate ({listings.length})</TabsTrigger>
            <TabsTrigger value="received" data-testid="tab-received-enquiries">Erhaltene Anfragen ({sellerEnqs.length})</TabsTrigger>
            <TabsTrigger value="sent" data-testid="tab-sent-enquiries">Gesendete Anfragen ({buyerEnqs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="listings">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Laden…</div>
            ) : listings.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">
                <p>Noch keine Inserate erstellt.</p>
                <Button className="mt-4" onClick={() => navigate('/listing/new?vertical=spare_parts')} data-testid="empty-create-btn">
                  <PlusCircle className="w-4 h-4 mr-2" /> Erstes Inserat erstellen
                </Button>
              </CardContent></Card>
            ) : (
              <>
                {(() => {
                  const eligibleCount = listings.filter(isRenewEligible).length;
                  if (eligibleCount === 0 && selectedIds.size === 0) return null;
                  return (
                    <div
                      className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200"
                      data-testid="bulk-renew-toolbar"
                    >
                      <div className="text-sm">
                        <strong>{eligibleCount}</strong> Inserate sind verlängerbar
                        {selectedIds.size > 0 && <span> · <strong>{selectedIds.size}</strong> ausgewählt</span>}
                      </div>
                      <div className="flex-1" />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={selectAllEligible}
                        disabled={eligibleCount === 0}
                        data-testid="bulk-select-eligible-btn"
                      >
                        Alle verlängerbaren auswählen
                      </Button>
                      {selectedIds.size > 0 && (
                        <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-clear-btn">
                          Auswahl löschen
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => { setBulkRenewDiscount(true); setBulkRenewOpen(true); }}
                        disabled={selectedIds.size === 0}
                        data-testid="bulk-renew-btn"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        {selectedIds.size > 0 ? `Verlängern (${selectedIds.size})` : 'Verlängern'}
                      </Button>
                    </div>
                  );
                })()}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {listings.map(l => (
                  <Card key={l.listing_id} data-testid={`my-listing-${l.listing_id}`}>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2">
                          {isRenewEligible(l) && (
                            <Checkbox
                              checked={selectedIds.has(l.listing_id)}
                              onCheckedChange={() => toggleSelect(l.listing_id)}
                              className="mt-1"
                              data-testid={`select-listing-${l.listing_id}`}
                              aria-label="Für Bulk-Verlängerung auswählen"
                            />
                          )}
                          <div>
                            <h3 className="font-semibold">
                              <Link to={`/listing/${l.listing_id}`} className="hover:underline">{l.title}</Link>
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              {l.vertical === 'machines' ? 'Maschine' : 'Ersatzteil'} ·{' '}
                              {l.price_eur != null ? `€ ${Number(l.price_eur).toLocaleString('de-DE')}` : 'Auf Anfrage'}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge variant={STATUS_COLOR[l.status] || 'outline'}>{STATUS_LABEL[l.status] || l.status}</Badge>
                          <ExpiryBadge listing={l} />
                          {l.visibility === 'group_only' && (
                            <Badge variant="outline" className="gap-1 border-orange-300 text-orange-700 bg-orange-50 text-xs" data-testid={`visibility-badge-${l.listing_id}`}>
                              <Users className="w-3 h-3" /> Nur Gruppe
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/listing/${l.listing_id}/edit`)} data-testid={`edit-${l.listing_id}`}>
                          <Edit className="w-3 h-3 mr-1" /> Bearbeiten
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleVisibility(l)}
                          data-testid={`toggle-visibility-${l.listing_id}`}
                          title={l.visibility === 'group_only' ? 'Öffentlich machen' : 'Nur in Gruppe sichtbar machen'}
                        >
                          {l.visibility === 'group_only' ? (
                            <><Globe className="w-3 h-3 mr-1" /> Öffentlich</>
                          ) : (
                            <><Users className="w-3 h-3 mr-1" /> Nur Gruppe</>
                          )}
                        </Button>
                        {l.status === 'draft' && (
                          <Button size="sm" onClick={() => handleAction(publishListing, l.listing_id, 'Veröffentlicht')} data-testid={`publish-${l.listing_id}`}>
                            <Send className="w-3 h-3 mr-1" />Veröffentlichen
                          </Button>
                        )}
                        {l.status === 'active' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleAction(markListingSold, l.listing_id, 'Als verkauft markiert')} data-testid={`sold-${l.listing_id}`}>
                              Verkauft
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleAction(archiveListing, l.listing_id, 'Archiviert')} data-testid={`archive-${l.listing_id}`}>
                              Archivieren
                            </Button>
                            {(() => {
                              const d = _daysUntil(l.expires_at);
                              return d !== null && d <= 14 ? (
                                <Button size="sm" variant="outline" onClick={() => openRenewDialog(l)} data-testid={`renew-${l.listing_id}`}>
                                  <RefreshCw className="w-3 h-3 mr-1" /> Verlängern
                                </Button>
                              ) : null;
                            })()}
                          </>
                        )}
                        {l.status === 'expired' && (
                          <Button size="sm" onClick={() => openRenewDialog(l)} data-testid={`renew-${l.listing_id}`}>
                            <RefreshCw className="w-3 h-3 mr-1" /> Verlängern
                          </Button>
                        )}
                      </div>
                      {l.enquiry_count > 0 && (
                        <p className="text-xs text-muted-foreground pt-1">
                          <MessageSquare className="w-3 h-3 inline mr-1" />{l.enquiry_count} Anfrage(n)
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="received">
            <EnquiryList
              enquiries={sellerEnqs}
              emptyText="Noch keine Anfragen erhalten."
              role="seller"
              onRespond={(enq) => { setRespEnq(enq); setRespOpen(true); }}
              onAccept={handleAcceptEnquiry}
              onCounter={(enq) => openCounterDialog(enq, 'seller')}
              onRejectCounter={handleRejectCounter}
            />
          </TabsContent>

          <TabsContent value="sent">
            <EnquiryList
              enquiries={buyerEnqs}
              emptyText="Noch keine Anfragen gesendet."
              role="buyer"
              onAccept={handleAcceptEnquiry}
              onCounter={(enq) => openCounterDialog(enq, 'buyer')}
              onRejectCounter={handleRejectCounter}
            />
          </TabsContent>
        </Tabs>

        <Dialog open={respOpen} onOpenChange={setRespOpen}>
          <DialogContent data-testid="respond-dialog">
            <DialogHeader>
              <DialogTitle>Antworten</DialogTitle>
              <DialogDescription>{respEnq?.message}</DialogDescription>
            </DialogHeader>
            <Textarea rows={6} value={respMsg} onChange={e => setRespMsg(e.target.value)} placeholder="Deine Antwort…" data-testid="respond-textarea" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRespOpen(false)}>Abbrechen</Button>
              <Button onClick={submitResponse} data-testid="respond-submit">Antwort senden</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Renew Dialog with 10% Price-Reduction Suggestion */}
        <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
          <DialogContent data-testid="renew-dialog">
            <DialogHeader>
              <DialogTitle>Inserat verlängern</DialogTitle>
              <DialogDescription>
                {renewListingState?.title} — um weitere 180 Tage sichtbar halten
              </DialogDescription>
            </DialogHeader>
            {renewListingState?.price_eur && renewListingState.price_eur > 0 ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <Checkbox
                    id="renew-discount-checkbox"
                    checked={renewWithDiscount}
                    onCheckedChange={(v) => setRenewWithDiscount(Boolean(v))}
                    data-testid="renew-discount-checkbox"
                  />
                  <label htmlFor="renew-discount-checkbox" className="text-sm cursor-pointer flex-1">
                    <div className="font-medium text-amber-900">Mit 10 % Rabatt neu listen</div>
                    <div className="text-xs text-amber-800 mt-1">Empfohlen, um die Sell-Through-Rate zu steigern.</div>
                    <div className="mt-2 text-sm">
                      Aktuell: <span className="line-through text-muted-foreground">€ {Number(renewListingState.price_eur).toLocaleString('de-DE')}</span>{' '}
                      → <strong className="text-amber-900" data-testid="renew-new-price">
                        € {Number(renewListingState.price_eur * 0.9).toLocaleString('de-DE', { maximumFractionDigits: 2 })}
                      </strong>
                    </div>
                  </label>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Dieses Inserat hat keinen Preis — es wird nur die Laufzeit verlängert.</p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenewOpen(false)} disabled={renewing} data-testid="renew-cancel">Abbrechen</Button>
              <Button onClick={submitRenew} disabled={renewing} data-testid="renew-confirm">
                <RefreshCw className={`w-3 h-3 mr-1 ${renewing ? 'animate-spin' : ''}`} />
                {renewing ? 'Verlängere…' : 'Jetzt verlängern'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Counter-Bid Dialog */}
        <Dialog open={counterOpen} onOpenChange={setCounterOpen}>
          <DialogContent data-testid="counter-bid-dialog">
            <DialogHeader>
              <DialogTitle>
                {counterRole === 'seller' ? 'Gegengebot an Käufer senden' : 'Gegengebot an Verkäufer senden'}
              </DialogTitle>
              <DialogDescription>
                {counterEnq?.listing_title ? `Zum Inserat: ${counterEnq.listing_title}` : 'Gegenangebot zur Anfrage'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Current situation summary */}
              {counterEnq && (
                <div className="p-3 bg-muted rounded text-xs space-y-1" data-testid="counter-current-offer">
                  <div className="font-medium">Aktuelles Angebot</div>
                  {counterEnq.counter_bid_price_EUR != null ? (
                    <div>
                      Gegengebot ({counterEnq.counter_bid_by === 'seller' ? 'Verkäufer' : 'Käufer'}):{' '}
                      <span className="font-medium text-orange-600">
                        € {Number(counterEnq.counter_bid_price_EUR).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                      </span>
                      {counterEnq.counter_bid_qty != null && <> · Menge: <strong>{counterEnq.counter_bid_qty}</strong></>}
                      {counterEnq.counter_bid_incoterm && <> · {counterEnq.counter_bid_incoterm}</>}
                    </div>
                  ) : (
                    <div>
                      Käufer-Gebot:{' '}
                      <span className="font-medium">
                        € {counterEnq.bid_price_EUR != null ? Number(counterEnq.bid_price_EUR).toLocaleString('de-DE', { minimumFractionDigits: 2 }) : '—'}
                      </span>
                      {counterEnq.requested_qty != null && <> · Menge: <strong>{counterEnq.requested_qty}</strong></>}
                      {counterEnq.incoterm && <> · {counterEnq.incoterm}</>}
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="counter-price-input">Preis (EUR) *</Label>
                  <Input
                    id="counter-price-input"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={counterPrice}
                    onChange={(e) => setCounterPrice(e.target.value)}
                    data-testid="counter-price-input"
                    placeholder="z. B. 450.00"
                  />
                </div>
                <div>
                  <Label htmlFor="counter-qty-input">Menge (optional)</Label>
                  <Input
                    id="counter-qty-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={counterQty}
                    onChange={(e) => setCounterQty(e.target.value)}
                    data-testid="counter-qty-input"
                    placeholder="z. B. 3"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="counter-incoterm-select">Incoterm (optional)</Label>
                <Select value={counterIncoterm} onValueChange={setCounterIncoterm}>
                  <SelectTrigger id="counter-incoterm-select" data-testid="counter-incoterm-select">
                    <SelectValue placeholder="Incoterm wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Keiner —</SelectItem>
                    {['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DDP'].map(i => (
                      <SelectItem key={i} value={i}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="counter-message-input">Nachricht (optional)</Label>
                <Textarea
                  id="counter-message-input"
                  rows={3}
                  value={counterMessage}
                  onChange={(e) => setCounterMessage(e.target.value)}
                  placeholder="Begründung oder zusätzliche Info…"
                  data-testid="counter-message-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCounterOpen(false)} disabled={counterSubmitting} data-testid="counter-cancel-btn">
                Abbrechen
              </Button>
              <Button onClick={submitCounterBid} disabled={counterSubmitting} data-testid="counter-submit-btn">
                <Handshake className="w-3 h-3 mr-1" />
                {counterSubmitting ? 'Sende…' : 'Gegengebot senden'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Renew Dialog */}
        <Dialog open={bulkRenewOpen} onOpenChange={setBulkRenewOpen}>
          <DialogContent data-testid="bulk-renew-dialog">
            <DialogHeader>
              <DialogTitle>Mehrere Inserate verlängern</DialogTitle>
              <DialogDescription>
                {selectedIds.size} Inserate um weitere 180 Tage sichtbar halten.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
                <Checkbox
                  id="bulk-renew-discount-checkbox"
                  checked={bulkRenewDiscount}
                  onCheckedChange={(v) => setBulkRenewDiscount(Boolean(v))}
                  data-testid="bulk-renew-discount-checkbox"
                />
                <label htmlFor="bulk-renew-discount-checkbox" className="text-sm cursor-pointer flex-1">
                  <div className="font-medium text-amber-900">Mit 10 % Rabatt neu listen (nur Inserate mit Preis)</div>
                  <div className="text-xs text-amber-800 mt-1">
                    Preislos geführte Inserate werden ohne Preisänderung verlängert.
                  </div>
                </label>
              </div>
              <div className="text-xs text-muted-foreground">
                Archivierte / verkaufte / fremde Inserate werden automatisch übersprungen.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkRenewOpen(false)} disabled={bulkRenewing} data-testid="bulk-renew-cancel">
                Abbrechen
              </Button>
              <Button onClick={submitBulkRenew} disabled={bulkRenewing || selectedIds.size === 0} data-testid="bulk-renew-confirm">
                <RefreshCw className={`w-3 h-3 mr-1 ${bulkRenewing ? 'animate-spin' : ''}`} />
                {bulkRenewing ? 'Verlängere…' : `Jetzt ${selectedIds.size} verlängern`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CSV Bulk Import Dialog (spare parts) */}
        <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) resetImportDialog(); }}>
          <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
            <DialogHeader>
              <DialogTitle>Ersatzteile per CSV importieren</DialogTitle>
              <DialogDescription>
                Laden Sie bis zu 500 Ersatzteil-Inserate gleichzeitig hoch. Verwenden Sie die Vorlage, um Spaltenfehler zu vermeiden.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={downloadImportTemplate} data-testid="bulk-import-template-btn">
                  <Download className="w-4 h-4 mr-2" /> CSV-Vorlage herunterladen
                </Button>
                <span className="text-xs text-muted-foreground">(UTF-8, Trennzeichen , oder ;)</span>
              </div>
              <div className="border-2 border-dashed rounded-lg p-6 text-center bg-muted/30">
                <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                  className="mx-auto block text-sm"
                  data-testid="bulk-import-file-input"
                />
                {importFile && <p className="text-xs text-muted-foreground mt-2">Ausgewählt: <strong>{importFile.name}</strong> ({Math.round(importFile.size / 1024)} KB)</p>}
              </div>
              {importResult && (
                <div className="p-4 rounded-lg bg-muted space-y-1 text-sm" data-testid="bulk-import-result">
                  {importResult.dry_run ? (
                    <>
                      <p className="font-medium">Vorschau-Modus (nichts gespeichert)</p>
                      <p>Zeilen gesamt: <strong>{importResult.total_rows}</strong></p>
                      <p>Gültig: <strong className="text-green-700">{importResult.valid_count}</strong></p>
                      <p>Fehler: <strong className="text-red-700">{importResult.error_count}</strong></p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-green-700">Importiert: {importResult.created}</p>
                      <p>Fehler: <strong className="text-red-700">{importResult.error_count}</strong></p>
                    </>
                  )}
                  {importResult.errors?.length > 0 && (
                    <ul className="mt-2 max-h-40 overflow-auto text-xs text-red-700 list-disc list-inside">
                      {importResult.errors.slice(0, 20).map((err, i) => <li key={i}>{err}</li>)}
                      {importResult.errors.length > 20 && <li>… und {importResult.errors.length - 20} weitere</li>}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing} data-testid="bulk-import-close">Schließen</Button>
              <Button variant="outline" onClick={() => submitBulkImport(true)} disabled={!importFile || importing} data-testid="bulk-import-dry-run">
                Vorschau
              </Button>
              <Button onClick={() => submitBulkImport(false)} disabled={!importFile || importing} data-testid="bulk-import-submit">
                <Upload className={`w-3 h-3 mr-1 ${importing ? 'animate-pulse' : ''}`} />
                {importing ? 'Lade…' : 'Importieren'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

function EnquiryList({ enquiries, emptyText, role, onRespond, onAccept, onCounter, onRejectCounter }) {
  if (enquiries.length === 0) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground">{emptyText}</CardContent></Card>;
  }
  const fmtEur = (v) => Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const statusVariant = (s) => {
    if (s === 'accepted') return 'default';
    if (s === 'open') return 'default';
    if (s === 'countered_by_seller' || s === 'countered_by_buyer') return 'outline';
    return 'secondary';
  };
  const statusLabel = (s) => {
    const map = {
      open: 'offen',
      responded: 'beantwortet',
      countered_by_seller: 'Gegengebot Verkäufer',
      countered_by_buyer: 'Gegengebot Käufer',
      accepted: 'angenommen',
      closed: 'geschlossen',
    };
    return map[s] || s;
  };

  // Is it this user's turn to accept/counter/reject?
  const canActOnCounter = (e) => {
    if (role === 'seller') return e.status === 'open' || e.status === 'responded' || e.status === 'countered_by_buyer';
    if (role === 'buyer') return e.status === 'countered_by_seller';
    return false;
  };
  const canAcceptAsBuyer = (e) => role === 'buyer' && e.status === 'countered_by_seller';
  const canAcceptAsSeller = (e) => role === 'seller' && (e.status === 'open' || e.status === 'responded' || e.status === 'countered_by_buyer');

  return (
    <div className="space-y-3">
      {enquiries.map(e => (
        <Card key={e.enquiry_id} data-testid={`enquiry-${e.enquiry_id}`}>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline">{ENQUIRY_TYPE_LABEL[e.enquiry_type] || e.enquiry_type}</Badge>
                  <Badge variant={statusVariant(e.status)} data-testid={`enq-status-${e.enquiry_id}`}>{statusLabel(e.status)}</Badge>
                  {e.incoterm && <Badge variant="outline" className="gap-1" data-testid={`enq-incoterm-${e.enquiry_id}`}>{e.incoterm}</Badge>}
                </div>
                <p className="text-sm mt-2">
                  <Link to={`/listing/${e.listing_id}`} className="text-blue-500 hover:underline">
                    {e.listing_title ? `Zum Inserat: ${e.listing_title}` : 'Zum Inserat'}
                  </Link>
                </p>
                {role === 'seller' && <p className="text-xs text-muted-foreground">Von: {e.buyer_name} ({e.buyer_email})</p>}

                {/* Commercial data row (parity with material requests) */}
                {(e.requested_qty != null || e.bid_price_EUR != null || e.delivery_site) && (
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid={`enq-commercials-${e.enquiry_id}`}>
                    {e.requested_qty != null && (
                      <span><span className="text-muted-foreground">Menge:</span> <span className="font-medium">{e.requested_qty}</span></span>
                    )}
                    {e.bid_price_EUR != null && (
                      <span><span className="text-muted-foreground">Gebot:</span> <span className="font-medium text-orange-600">{fmtEur(e.bid_price_EUR)} EUR</span></span>
                    )}
                    {e.requested_qty > 0 && e.bid_price_EUR != null && (
                      <span><span className="text-muted-foreground">je Einheit:</span> <span className="font-medium">{fmtEur(Number(e.bid_price_EUR) / Number(e.requested_qty))} EUR</span></span>
                    )}
                    {e.delivery_site && (
                      <span><span className="text-muted-foreground">Lieferung:</span> <span className="font-medium">{e.delivery_site.site_name} ({e.delivery_site.city})</span></span>
                    )}
                  </div>
                )}

                {/* Counter-bid details */}
                {e.counter_bid_price_EUR != null && (e.status === 'countered_by_seller' || e.status === 'countered_by_buyer') && (
                  <div
                    className="mt-3 p-3 border border-orange-300 bg-orange-50 rounded text-sm"
                    data-testid={`counter-bid-info-${e.enquiry_id}`}
                  >
                    <p className="font-medium text-orange-900 mb-1 flex items-center gap-1">
                      <Handshake className="w-3 h-3" />
                      Aktives Gegengebot ({e.counter_bid_by === 'seller' ? 'Verkäufer' : 'Käufer'})
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                      <span>
                        <span className="text-muted-foreground">Preis:</span>{' '}
                        <span className="font-medium text-orange-700" data-testid={`counter-price-${e.enquiry_id}`}>
                          {fmtEur(e.counter_bid_price_EUR)} EUR
                        </span>
                      </span>
                      {e.counter_bid_qty != null && (
                        <span><span className="text-muted-foreground">Menge:</span> <span className="font-medium">{e.counter_bid_qty}</span></span>
                      )}
                      {e.counter_bid_incoterm && (
                        <span><span className="text-muted-foreground">Incoterm:</span> <span className="font-medium">{e.counter_bid_incoterm}</span></span>
                      )}
                    </div>
                    {e.counter_bid_message && (
                      <p className="text-xs mt-1 whitespace-pre-wrap">{e.counter_bid_message}</p>
                    )}
                  </div>
                )}

                {/* Counter-bid history (multi-round) */}
                {Array.isArray(e.counter_bid_history) && e.counter_bid_history.length > 1 && (
                  <details className="mt-2 text-xs" data-testid={`counter-history-${e.enquiry_id}`}>
                    <summary className="cursor-pointer text-muted-foreground">
                      Verhandlungsverlauf ({e.counter_bid_history.length} Runden)
                    </summary>
                    <ul className="mt-1 pl-4 list-disc space-y-1">
                      {e.counter_bid_history.map((h, idx) => (
                        <li key={idx}>
                          <span className="font-medium">{h.by === 'seller' ? 'Verkäufer' : 'Käufer'}:</span>{' '}
                          {fmtEur(h.price_EUR)} EUR
                          {h.qty != null && <> · Menge {h.qty}</>}
                          {h.incoterm && <> · {h.incoterm}</>}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                <p className="text-sm mt-2 whitespace-pre-wrap">{e.message}</p>
                {e.preferred_date && <p className="text-xs text-muted-foreground mt-1">Wunschtermin: {e.preferred_date}</p>}
                {e.contact_phone && <p className="text-xs text-muted-foreground">Telefon: {e.contact_phone}</p>}
                {e.response_message && (
                  <div className="mt-3 p-3 bg-muted rounded text-sm">
                    <p className="font-medium mb-1">Antwort:</p>
                    <p className="whitespace-pre-wrap">{e.response_message}</p>
                  </div>
                )}
                {e.status === 'closed' && e.closed_reason === 'counter_bid_rejected' && (
                  <p className="text-xs text-red-600 mt-1">Gegengebot wurde abgelehnt.</p>
                )}
              </div>
              <div className="flex flex-col gap-2 ml-2">
                {/* Seller actions on received enquiries */}
                {role === 'seller' && onRespond && e.status === 'open' && (
                  <Button size="sm" variant="outline" onClick={() => onRespond(e)} data-testid={`respond-btn-${e.enquiry_id}`}>
                    <Send className="w-3 h-3 mr-1" />Antworten
                  </Button>
                )}
                {/* Accept button — role dependent */}
                {onAccept && (canAcceptAsSeller(e) || canAcceptAsBuyer(e)) && (
                  <Button size="sm" onClick={() => onAccept(e)} data-testid={`accept-btn-${e.enquiry_id}`}>
                    Annehmen
                  </Button>
                )}
                {/* Counter-bid button */}
                {onCounter && canActOnCounter(e) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onCounter(e)}
                    data-testid={`counter-btn-${e.enquiry_id}`}
                  >
                    <Handshake className="w-3 h-3 mr-1" />Gegengebot
                  </Button>
                )}
                {/* Reject counter button — only when it's this role's turn and a counter is live */}
                {onRejectCounter && canActOnCounter(e) &&
                  (e.status === 'countered_by_seller' || e.status === 'countered_by_buyer') && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onRejectCounter(e)}
                    data-testid={`reject-counter-btn-${e.enquiry_id}`}
                  >
                    <X className="w-3 h-3 mr-1" />Ablehnen
                  </Button>
                )}
                {role === 'buyer' && e.status === 'accepted' && e.transaction_id && (
                  <Badge variant="default" data-testid={`txn-badge-${e.enquiry_id}`}>
                    Transaktion angelegt
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
